import { Stack, Typography } from '@ethings/ui-toolkit';
import { styled } from '@mui/material';
import { FC, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { MiddleSpinner } from '../../components';
import { useSmallScreen, useTitle } from '../../hooks';
import { Portal } from '../../types';
import { portalDefinitions } from '../../util';
import { WelcomePageLayout } from './welcome-page-layout';

interface Props {
  accessiblePortals: Portal[];
  renderLogo: (smallScreen: boolean) => JSX.Element;
  renderText: (smallScreen: boolean) => JSX.Element;
  renderTitle: (smallScreen: boolean) => JSX.Element;
  title: string;
}

export const WelcomePageView: FC<Props> = ({ accessiblePortals, renderLogo, renderText, renderTitle, title }) => {
  const smallScreen = useSmallScreen();
  useTitle(title);
  const { t } = useTranslation(['common']);

  if (accessiblePortals.length === 1) {
    window.location.href = portalDefinitions[accessiblePortals[0]].path;

    return <MiddleSpinner />;
  }

  const portals = accessiblePortals.length === 0 ? <></> : (
    <Stack spacing={1} alignItems="center">
      <Typography variant="caption">{t('common:component.navigation.switch-to.labels')}</Typography>
      {accessiblePortals.map((portal) => {
        const portalDefinition = portalDefinitions[portal];

        const Icon = portalDefinition.icon;
        return (
          <PortalItem key={portal} href={portalDefinition.path}>
            <Icon />
            {t(portalDefinition.label)}
          </PortalItem>
        );
      })}
    </Stack>
  );

  return <WelcomePageLayout
    portals={portals}
    logo={renderLogo(smallScreen)}
    text={renderText(smallScreen)}
    title={renderTitle(smallScreen)}
  />;
};

const PortalItem = styled('a')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  color: 'inherit',
  textDecoration: 'none',
  gap: theme.spacing(1),
  'svg': {
    color: theme.palette.primary.main,
  },
}));
